/**
 * useSafeFetch — hardened data-fetching hook.
 *
 * Extracts the pattern from Dashboard.jsx et al.:
 *   - cancellation-safe (won't setState on unmounted components)
 *   - list-aware (unwraps `{data:[…]}` / `{items:[…]}` / `{bookings:[…]}`
 *     when the caller expects a list, guarantees Array.isArray safety)
 *   - error-tolerant (never throws to the caller, exposes the last error
 *     via a friendly formatApiError string)
 *   - refetch()able (returns a callback the UI can hook up to a refresh
 *     button or effect dep)
 *
 * Usage — list endpoint:
 *   const { data: bookings, loading, error, refetch } = useSafeFetch(
 *     "/bookings/me", { defaultValue: [], asList: true }
 *   );
 *
 * Usage — object endpoint:
 *   const { data: profile, loading, error } = useSafeFetch("/auth/me");
 *
 * Usage — conditional (skip until enabled):
 *   const q = useSafeFetch("/admin/stats", { enabled: user?.role === "admin" });
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "./api";

/** Unwrap a common list envelope from the server response. */
function unwrapList(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    for (const key of ["items", "data", "bookings", "results", "records"]) {
      if (Array.isArray(body[key])) return body[key];
    }
  }
  return null;
}

export function useSafeFetch(path, options = {}) {
  const {
    defaultValue = null,
    asList = false,
    enabled = true,
    params = null,
  } = options;

  const [data, setData] = useState(asList ? (defaultValue ?? []) : defaultValue);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState("");

  // Stable primitive key for params — avoids re-fetching on unrelated re-renders.
  const paramsKey = params ? JSON.stringify(params) : "";
  const cancelledRef = useRef(false);

  const run = useCallback(async () => {
    if (!enabled || !path) return;
    cancelledRef.current = false;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(path, params ? { params } : undefined);
      if (cancelledRef.current) return;
      if (asList) {
        const list = unwrapList(res?.data);
        setData(list ?? (Array.isArray(defaultValue) ? defaultValue : []));
      } else {
        setData(res?.data ?? defaultValue);
      }
    } catch (err) {
      if (cancelledRef.current) return;
      setError(formatApiError(err?.response?.data?.detail) || err?.message || "Request failed");
      setData(asList ? (Array.isArray(defaultValue) ? defaultValue : []) : defaultValue);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, paramsKey, enabled, asList]);

  useEffect(() => {
    cancelledRef.current = false;
    run();
    return () => { cancelledRef.current = true; };
  }, [run]);

  return { data, loading, error, refetch: run, setData };
}

export default useSafeFetch;
